// 市场分析页：Python CDP 采集、分页商品行情、价格监控、同开区比价、采集任务和价格规则。

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUpDown } from "lucide-react";
import { apiGet, apiPost, APIError } from "@/api/client";
import { API_DASHBOARD, API_MARKET_ANALYSIS, apiCanonicalGameServers } from "@/api/endpoints";
import { Empty } from "@/components/ui/Empty";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { QueryView } from "@/components/ui/QueryView";
import { toast } from "@/components/ui/Sonner";
import { StatusBadge, type StatusVariant } from "@/components/ui/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { useSession } from "@/hooks/useSession";
import { cn } from "@/lib/cn";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";

type MarketTab = "overview" | "baseline" | "watch" | "compare" | "tasks" | "rules";
type MarketListSort = "min_price_asc" | "min_price_desc";

interface MarketSearch {
  tab?: MarketTab;
  game_id?: string;
  server_id?: string;
  q?: string;
  cursor?: number;
  status_cursor?: number;
  run_cursor?: number;
  watch_cursor?: number;
  plan_cursor?: number;
  item_limit?: number;
  status_limit?: number;
  run_limit?: number;
  item_sort?: MarketListSort;
  watch_sort?: MarketListSort;
}

interface PageResponse<T> {
  ok: boolean;
  items: T[];
  count?: number;
  limit?: number;
  cursor?: number;
  total?: number;
  next_cursor?: number;
}

interface MarketStatusRecord {
  game_id: string;
  canonical_server_id: string;
  canonical_server_name?: string;
  category: string;
  last_source?: string;
  last_mode?: string;
  last_agent_id?: string;
  server_id?: string;
  server_name?: string;
  last_status?: string;
  last_started_at?: number;
  last_finished_at?: number;
  last_item_count?: number;
  last_changed_count?: number;
  last_error?: string;
  current_count: number;
}

interface MarketItemRecord {
  game_id: string;
  canonical_server_id: string;
  canonical_server_name?: string;
  category: string;
  item_key: string;
  item_id?: string;
  name: string;
  min_price: number;
  raw_min_price?: number;
  effective_min_price?: number;
  reference_price?: number;
  suspect_type?: string;
  suspect_score?: number;
  suspect_reasons?: string;
  avg_price: number;
  median_price: number;
  listing_count: number;
  total_quantity: number;
  lowest_commodity_id?: string;
  lowest_seller_id?: string;
  lowest_seller_name?: string;
  lowest_quantity: number;
  last_seen_at: number;
  data_source?: string;
}

interface MarketItemHistoryRecord {
  game_id: string;
  canonical_server_id: string;
  canonical_server_name?: string;
  category: string;
  item_key: string;
  day: string;
  item_id?: string;
  name: string;
  sample_count: number;
  daily_min_price: number;
  daily_min_commodity_id?: string;
  daily_min_quantity: number;
  daily_min_seen_at: number;
  latest_min_price: number;
  daily_raw_min_price?: number;
  daily_effective_min_price?: number;
  latest_avg_price: number;
  latest_median_price: number;
  latest_listing_count: number;
  latest_total_quantity: number;
  latest_seen_at: number;
}

interface MarketRunRecord {
  id: number;
  game_id: string;
  canonical_server_id: string;
  server_id: string;
  category: string;
  status: string;
  started_at: number;
  finished_at: number;
  page_count: number;
  item_count: number;
  changed_count: number;
  error?: string;
  source?: string;
  mode?: string;
  agent_id?: string;
  filter_hash?: string;
  metadata_json?: string;
}

interface MarketPlanRecord {
  id: number;
  name: string;
  enabled: boolean;
  game_id: string;
  target_kind: string;
  target_json: string;
  mode: string;
  pace_preset: string;
  interval_seconds: number;
  agent_strategy: string;
  max_failures: number;
  updated_at: number;
}

interface MarketTargetRecord {
  id: number;
  plan_id: number;
  game_id: string;
  server_id: string;
  canonical_server_id: string;
  category: string;
  keyword: string;
  item_key: string;
  unit_price: string;
  order_price: string;
  min_price: string;
  max_price: string;
  limit_pages: number;
  baseline_scope: boolean;
  collection_scope_hash: string;
  priority: number;
  next_due_at: number;
  last_run_id: number;
  last_status?: string;
  last_error?: string;
  failure_count: number;
  backoff_until: number;
}

interface MarketWatchItemRecord {
  id: number;
  material_watch_id?: number;
  material_item_id?: number;
  enabled: boolean;
  game_id: string;
  server_id: string;
  canonical_server_id: string;
  server_name?: string;
  category: string;
  item_name: string;
  item_key: string;
  interval_seconds: number;
  limit_pages: number;
  pace_preset: string;
  agent_id?: string;
  unit_price: string;
  order_price: string;
  min_price: string;
  max_price: string;
  next_due_at: number;
  last_run_id: number;
  last_status?: string;
  last_error?: string;
  last_full_at: number;
  min_price_value: number;
  reference_price: number;
  median_price: number;
  listing_count: number;
  suspect_type?: string;
  suspect_score?: number;
  data_source?: string;
}

interface MarketPriceRuleRecord {
  game_id: string;
  canonical_server_id: string;
  category: string;
  item_key: string;
  rule_json: string;
  note?: string;
  updated_by?: string;
  updated_at: number;
}

interface OpenDayCompareRecord {
  game_id: string;
  server_id: string;
  server_name?: string;
  open_batch: number;
  has_listing: boolean;
  effective_min_price: number;
  raw_min_price: number;
  reference_price: number;
  median_price: number;
  avg_price: number;
  listing_count: number;
  total_quantity: number;
  suspect_type?: string;
  suspect_score?: number;
  last_seen_at: number;
  last_full_at: number;
  data_age_seconds: number;
  rank_by_effective_min?: number;
  delta_to_cheapest: number;
  delta_to_median: number;
  stock_state: string;
  needs_collect: boolean;
  data_source?: string;
}

interface MarketAgentCapability {
  agent_id: string;
  authenticated: boolean;
  state?: string;
  peer?: string;
  peer_host?: string;
  hostname?: string;
  game_id?: string;
  server_id?: string;
  canonical_server_id?: string;
  server_name?: string;
  role_count?: number;
  market_cdp_enabled?: boolean;
  box996_installed?: boolean;
  box996_exe_path?: string;
  box996_source?: string;
  collector_version?: string;
  cdp_port?: number;
  cdp_ports?: number[];
  cdp_blocked_reason?: string;
  account_cdp_binding_count?: number;
  account_cdp_box_exe_paths?: string[];
  account_cdp_ports?: number[];
  busy?: boolean;
  reason?: string;
  last_role_seen_at?: number;
}

interface MarketAgentCapabilitiesResponse {
  ok: boolean;
  items: MarketAgentCapability[];
  count: number;
}

interface MarketAgentOption {
  agent_id: string;
  hostname?: string;
  state?: string;
  authenticated?: boolean;
  busy?: boolean;
  market_cdp_enabled?: boolean;
  box996_installed?: boolean;
  box996_exe_path?: string;
  cdp_port?: number;
  cdp_ports?: number[];
  cdp_blocked_reason?: string;
  account_cdp_binding_count?: number;
  account_cdp_box_exe_paths?: string[];
  account_cdp_ports?: number[];
}

interface MarketSchedulerResponse {
  ok: boolean;
  leases: unknown[];
  targets: MarketTargetRecord[];
  capabilities?: MarketAgentCapability[];
}

interface MarketServerDirectoryEntry {
  server_id: string;
  server_name?: string;
  canonical_server_id?: string;
}

interface MarketServerDirectoryRefreshResponse {
  ok: boolean;
  item: {
    game_id: string;
    count: number;
    servers: MarketServerDirectoryEntry[];
    updated_at: number;
  };
  agent?: Record<string, unknown>;
}

interface MarketBulkTargetResponse {
  ok: boolean;
  created: number;
  updated: number;
  skipped?: number;
  batch_key?: string;
  items: MarketTargetRecord[];
}

interface DashboardAgentInfo {
  agent_id: string;
  hostname?: string;
  state?: string;
  last_seen_ms?: number;
}

interface DashboardResponse {
  ok: boolean;
  agents: DashboardAgentInfo[];
}

interface MarketCollectionRequest {
  source: "python_cdp";
  account_id?: number;
  agent_id: string;
  game_id: string;
  server_id: string;
  category: string;
  mode: "incremental" | "full";
  limit_pages?: number;
  pace_preset: "balanced" | "safe" | "fast";
  keyword?: string;
  equip_type_id?: string;
  unit_price?: string;
  order_price?: string;
  min_price?: string;
  max_price?: string;
  port?: number;
}

interface MarketCollectionResponse {
  ok: boolean;
  run_id: number;
  status: string;
}

interface MarketGameOption {
  game_id: string;
  display_name?: string;
  source?: string;
  server_count: number;
  last_seen_at?: number;
}

interface MarketGamesResponse {
  ok: boolean;
  query: string;
  items: MarketGameOption[];
  count: number;
  limit: number;
}

interface GameServerOption {
  game_id: string;
  server_id: string;
  server_name?: string;
  display_name?: string;
  alias?: string;
  effective_main_server_id?: string;
  source?: string;
  role_count: number;
  member_count?: number;
  matched_server_id?: string;
  matched_server_name?: string;
  matched_alias?: string;
  last_seen_at?: number;
}

interface GameServersResponse {
  ok: boolean;
  game_id: string;
  query: string;
  items: GameServerOption[];
  count: number;
  limit: number;
}

interface CollectorForm {
  account_id: string;
  agent_id: string;
  port: string;
  mode: "incremental" | "full";
  pace_preset: "balanced" | "safe" | "fast";
  limit_pages: string;
  keyword: string;
  unit_price: "" | "asc" | "desc";
  order_price: "" | "asc" | "desc";
  min_price: string;
  max_price: string;
}

type HistoryMetric =
  | "daily_min_price"
  | "daily_effective_min_price"
  | "latest_avg_price"
  | "latest_median_price"
  | "latest_listing_count";

const HISTORY_METRICS: Array<{ value: HistoryMetric; label: string }> = [
  { value: "daily_min_price", label: "最低单价" },
  { value: "daily_effective_min_price", label: "参考最低" },
  { value: "latest_avg_price", label: "均单价" },
  { value: "latest_median_price", label: "中位" },
  { value: "latest_listing_count", label: "挂单数" },
];

const TAB_LABELS: Array<{ value: MarketTab; label: string }> = [
  { value: "overview", label: "总览" },
  { value: "baseline", label: "商品行情" },
  { value: "watch", label: "价格监控" },
  { value: "compare", label: "同开区比价" },
  { value: "tasks", label: "采集任务" },
  { value: "rules", label: "价格规则" },
];

function pickString(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t === "" ? undefined : t;
}

function pickNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v) && v >= 0) return Math.trunc(v);
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return Math.trunc(n);
  }
  return undefined;
}

function pickMarketListSort(v: unknown): MarketListSort | undefined {
  const value = pickString(v);
  return value === "min_price_asc" || value === "min_price_desc" ? value : undefined;
}

function parseMarketSearch(search: Record<string, unknown>): MarketSearch {
  const rawTab = pickString(search.tab);
  const tab = TAB_LABELS.some((item) => item.value === rawTab) ? (rawTab as MarketTab) : undefined;
  return {
    tab,
    game_id: pickString(search.game_id),
    server_id: pickString(search.server_id),
    q: pickString(search.q),
    cursor: pickNumber(search.cursor),
    status_cursor: pickNumber(search.status_cursor),
    run_cursor: pickNumber(search.run_cursor),
    watch_cursor: pickNumber(search.watch_cursor),
    plan_cursor: pickNumber(search.plan_cursor),
    item_limit: pickNumber(search.item_limit),
    status_limit: pickNumber(search.status_limit),
    run_limit: pickNumber(search.run_limit),
    item_sort: pickMarketListSort(search.item_sort),
    watch_sort: pickMarketListSort(search.watch_sort),
  };
}

export const Route = createFileRoute("/market-analysis")({
  validateSearch: parseMarketSearch,
  component: MarketAnalysisRouteComponent,
});

function MarketAnalysisRouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/market-analysis" });
  return (
    <MarketAnalysisView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

export function MarketAnalysisView({
  search,
  onChange,
}: {
  search: MarketSearch;
  onChange: (next: MarketSearch) => void;
}) {
  const queryClient = useQueryClient();
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const tab = search.tab ?? "overview";
  const itemLimit = search.item_limit ?? 50;
  const statusLimit = search.status_limit ?? 50;
  const runLimit = search.run_limit ?? 50;
  const [form, setForm] = useState<MarketSearch>(search);
  const [gameQuery, setGameQuery] = useState(search.game_id ?? "");
  const [serverQuery, setServerQuery] = useState(search.server_id ?? "");
  const [selected, setSelected] = useState<MarketItemRecord | null>(null);
  const [historyMetric, setHistoryMetric] = useState<HistoryMetric>("daily_min_price");
  const [collector, setCollector] = useState<CollectorForm>({
    account_id: "",
    agent_id: "",
    port: "",
    mode: "full",
    pace_preset: "balanced",
    limit_pages: "0",
    keyword: "",
    unit_price: "asc",
    order_price: "",
    min_price: "",
    max_price: "",
  });
  const [planForm, setPlanForm] = useState({ name: "", interval_seconds: "86400", server_id: "" });
  const [ruleForm, setRuleForm] = useState({
    category: "equipment",
    item_key: "",
    rule_json: "{\"display_score_threshold\":40,\"isolated_lowest_max_sellers\":1,\"isolated_lowest_score\":20}",
    note: "",
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      game_id: search.game_id,
      server_id: search.server_id,
      q: search.q,
    }));
    setGameQuery(search.game_id ?? "");
    setServerQuery(search.server_id ?? "");
  }, [search.game_id, search.server_id, search.q]);

  const statusPath = `${API_MARKET_ANALYSIS}/status${queryString({
    game_id: search.game_id,
    limit: String(statusLimit),
    cursor: String(search.status_cursor ?? 0),
  })}`;
  const itemsPath = `${API_MARKET_ANALYSIS}/items${queryString({
    game_id: search.game_id,
    server_id: search.server_id,
    q: search.q,
    limit: String(itemLimit),
    cursor: String(search.cursor ?? 0),
    sort: search.item_sort,
  })}`;
  const runsPath = `${API_MARKET_ANALYSIS}/runs${queryString({
    game_id: search.game_id,
    server_id: search.server_id,
    limit: String(runLimit),
    cursor: String(search.run_cursor ?? 0),
  })}`;
  const plansPath = `${API_MARKET_ANALYSIS}/plans${queryString({
    game_id: search.game_id,
    limit: "50",
    cursor: String(search.plan_cursor ?? 0),
  })}`;
  const targetsPath = `${API_MARKET_ANALYSIS}/targets${queryString({
    game_id: search.game_id,
    limit: "50",
  })}`;
  const watchPath = `${API_MARKET_ANALYSIS}/watch-items${queryString({
    game_id: search.game_id,
    limit: "50",
    cursor: String(search.watch_cursor ?? 0),
    sort: search.watch_sort,
  })}`;
  const rulesPath = `${API_MARKET_ANALYSIS}/rules${queryString({
    game_id: search.game_id,
    server_id: search.server_id,
    item_key: search.q,
    limit: "100",
  })}`;
  const comparePath = `${API_MARKET_ANALYSIS}/open-day-compare${queryString({
    game_id: search.game_id,
    server_id: search.server_id,
    q: search.q,
    category: "equipment",
  })}`;
  const schedulerPath = `${API_MARKET_ANALYSIS}/scheduler/status${queryString({
    game_id: search.game_id,
  })}`;
  const agentCapabilitiesPath = `${API_MARKET_ANALYSIS}/agent-capabilities${queryString({
    game_id: search.game_id,
  })}`;
  const gameOptionsPath = `${API_MARKET_ANALYSIS}/games${queryString({
    q: gameQuery || form.game_id,
    limit: "20",
  })}`;
  const serverOptionsPath = form.game_id
    ? apiCanonicalGameServers(form.game_id, serverQuery || form.server_id || "", 20)
    : "";

  const statusQ = useQuery({
    queryKey: ["market-analysis-status", statusPath],
    queryFn: () => apiGet<PageResponse<MarketStatusRecord>>(statusPath),
    enabled: tab === "overview",
    refetchInterval: 60_000,
  });
  const itemsQ = useQuery({
    queryKey: ["market-analysis-items", itemsPath],
    queryFn: () => apiGet<PageResponse<MarketItemRecord>>(itemsPath),
    enabled: tab === "baseline",
  });
  const runsQ = useQuery({
    queryKey: ["market-analysis-runs", runsPath],
    queryFn: () => apiGet<PageResponse<MarketRunRecord>>(runsPath),
    enabled: tab === "tasks",
  });
  const plansQ = useQuery({
    queryKey: ["market-analysis-plans", plansPath],
    queryFn: () => apiGet<PageResponse<MarketPlanRecord>>(plansPath),
    enabled: tab === "tasks",
  });
  const targetsQ = useQuery({
    queryKey: ["market-analysis-targets", targetsPath],
    queryFn: () => apiGet<PageResponse<MarketTargetRecord>>(targetsPath),
    enabled: tab === "tasks",
  });
  const watchQ = useQuery({
    queryKey: ["market-analysis-watch", watchPath],
    queryFn: () => apiGet<PageResponse<MarketWatchItemRecord>>(watchPath),
    enabled: tab === "watch",
  });
  const rulesQ = useQuery({
    queryKey: ["market-analysis-rules", rulesPath],
    queryFn: () => apiGet<PageResponse<MarketPriceRuleRecord>>(rulesPath),
    enabled: tab === "rules",
  });
  const compareQ = useQuery({
    queryKey: ["market-analysis-compare", comparePath],
    enabled: tab === "compare" && Boolean(search.game_id && search.server_id && search.q),
    queryFn: () => apiGet<{ ok: boolean; items: OpenDayCompareRecord[] }>(comparePath),
  });
  const schedulerQ = useQuery({
    queryKey: ["market-analysis-scheduler", schedulerPath],
    queryFn: () => apiGet<MarketSchedulerResponse>(schedulerPath),
    enabled: tab === "tasks",
    refetchInterval: 30_000,
  });
  const agentsQ = useQuery({
    queryKey: ["market-analysis-agents"],
    queryFn: () => apiGet<DashboardResponse>(API_DASHBOARD),
    enabled: tab === "overview" || tab === "tasks",
    refetchInterval: 15_000,
  });
  const agentCapabilitiesQ = useQuery({
    queryKey: ["market-analysis-agent-capabilities", agentCapabilitiesPath],
    queryFn: () => apiGet<MarketAgentCapabilitiesResponse>(agentCapabilitiesPath),
    enabled: tab === "overview" || tab === "tasks",
    refetchInterval: 15_000,
  });
  const gameOptionsQ = useQuery({
    queryKey: ["market-analysis-game-options", gameOptionsPath],
    queryFn: () => apiGet<MarketGamesResponse>(gameOptionsPath),
    staleTime: 15_000,
  });
  const serverOptionsQ = useQuery({
    queryKey: ["market-analysis-server-options", form.game_id, serverOptionsPath],
    queryFn: () => apiGet<GameServersResponse>(serverOptionsPath),
    enabled: Boolean(form.game_id),
    staleTime: 15_000,
  });

  const onlineAgents = agentsQ.data?.agents ?? [];
  const agentOptions = useMemo(
    () => marketAgentOptions(agentCapabilitiesQ.data?.items, onlineAgents),
    [agentCapabilitiesQ.data?.items, onlineAgents],
  );
  const firstReadyAgent = agentOptions.find((agent) => marketAgentReady(agent))?.agent_id;
  const selectedAgentID = collector.agent_id || firstReadyAgent || agentOptions[0]?.agent_id || "";
  const selectedAgent = agentOptions.find((agent) => agent.agent_id === selectedAgentID);
  const gameComboOptions = (gameOptionsQ.data?.items ?? []).map((game) => ({
    value: game.game_id,
    label: game.display_name || game.game_id,
    meta: [game.display_name ? game.game_id : "", game.server_count ? `${game.server_count} 个区服` : "", game.source || ""]
      .filter(Boolean)
      .join(" · "),
  }));
  const serverComboOptions = (serverOptionsQ.data?.items ?? []).map((server) => serverComboOption(server));
  const statusServerLabel = useMemo(
    () => labelServerFromStatus(form.server_id, statusQ.data?.items),
    [form.server_id, statusQ.data?.items],
  );
  const selectedGameLabel = labelForValue(form.game_id, gameComboOptions);
  const selectedServerLabel = labelForServerValue(form.server_id, serverComboOptions) || statusServerLabel;

  const invalidateMarket = () => {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("market-analysis"),
    });
  };

  const collectMutation = useMutation({
    mutationFn: (body: MarketCollectionRequest) =>
      apiPost<MarketCollectionResponse>(`${API_MARKET_ANALYSIS}/collections`, body),
    onSuccess: (payload) => {
      toast.success("采集已排队", { description: `run #${payload.run_id}` });
      invalidateMarket();
    },
    onError: (err) => toast.error("启动采集失败", { description: errorText(err) }),
  });

  const planMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`${API_MARKET_ANALYSIS}/plans`, body),
    onSuccess: async (payload: unknown) => {
      const plan = payload as { item?: MarketPlanRecord };
      if (plan.item?.id && planForm.server_id) {
        await apiPost(`${API_MARKET_ANALYSIS}/targets`, {
          plan_id: plan.item.id,
          game_id: plan.item.game_id,
          server_id: planForm.server_id,
          category: "equipment",
          baseline_scope: true,
          interval_seconds: Number(planForm.interval_seconds) || 86400,
        });
      }
      toast.success("采集计划已保存");
      setPlanForm({ name: "", interval_seconds: "86400", server_id: "" });
      invalidateMarket();
    },
    onError: (err) => toast.error("保存采集计划失败", { description: errorText(err) }),
  });

  const runPlanMutation = useMutation({
    mutationFn: (planID: number) =>
      apiPost<MarketCollectionResponse>(
        `${API_MARKET_ANALYSIS}/plans/${planID}/run${queryString({ agent_id: selectedAgentID })}`,
        {},
      ),
    onSuccess: (payload) => {
      toast.success("计划已手动触发", { description: `run #${payload.run_id}` });
      invalidateMarket();
    },
    onError: (err) => toast.error("触发计划失败", { description: errorText(err) }),
  });

  const ruleMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`${API_MARKET_ANALYSIS}/rules`, body),
    onSuccess: () => {
      toast.success("价格规则已保存");
      invalidateMarket();
    },
    onError: (err) => toast.error("保存规则失败", { description: errorText(err) }),
  });

  const directoryRefreshMutation = useMutation({
    mutationFn: () =>
      apiPost<MarketServerDirectoryRefreshResponse>(`${API_MARKET_ANALYSIS}/server-directory/refresh`, {
        game_id: search.game_id,
        agent_id: selectedAgentID || undefined,
      }),
    onSuccess: (payload) => {
      toast.success("区服目录已刷新", { description: `${payload.item.count} 个区服` });
      invalidateMarket();
      void queryClient.invalidateQueries({ queryKey: ["market-analysis-server-options"] });
    },
    onError: (err) => toast.error("刷新区服目录失败", { description: errorText(err) }),
  });

  const baselineGenerateMutation = useMutation({
    mutationFn: () =>
      apiPost<MarketBulkTargetResponse>(`${API_MARKET_ANALYSIS}/baseline-targets/generate`, {
        game_id: search.game_id,
        interval_seconds: 86400,
        priority: 100,
      }),
    onSuccess: (payload) => {
      toast.success("基线目标已生成", { description: `新增 ${payload.created}，更新 ${payload.updated}` });
      invalidateMarket();
    },
    onError: (err) => toast.error("生成基线目标失败", { description: errorText(err) }),
  });

  const compareCollectMutation = useMutation({
    mutationFn: (request: { onlyProblems: boolean; refreshDirectory?: boolean }) =>
      apiPost<MarketBulkTargetResponse>(`${API_MARKET_ANALYSIS}/open-day-compare/collect`, {
        game_id: search.game_id,
        server_id: search.server_id,
        item_name: search.q,
        q: search.q,
        only_problems: request.onlyProblems,
        refresh_directory: request.refreshDirectory || undefined,
      }),
    onSuccess: (payload) => {
      toast.success("同开区补抓已排队", { description: `新增 ${payload.created}，更新 ${payload.updated}，跳过 ${payload.skipped ?? 0}` });
      invalidateMarket();
    },
    onError: (err) => toast.error("同开区补抓失败", { description: errorText(err) }),
  });

  const selectedScope = selected
    ? { game_id: selected.game_id, server_id: selected.canonical_server_id }
    : { game_id: search.game_id, server_id: search.server_id };
  const historyPath = selected
    ? `${API_MARKET_ANALYSIS}/items/${encodeURIComponent(selected.item_key)}/history${queryString({
        game_id: selectedScope.game_id,
        server_id: selectedScope.server_id,
        limit: "100",
      })}`
    : "";
  const historyQ = useQuery({
    queryKey: ["market-analysis-history", selected?.item_key, selectedScope],
    enabled: Boolean(selected && selectedScope.game_id && selectedScope.server_id),
    queryFn: () => apiGet<{ ok: boolean; items: MarketItemHistoryRecord[] }>(historyPath),
  });

  const latestStatus = useMemo(() => {
    const rows = statusQ.data?.items ?? [];
    return rows.reduce<MarketStatusRecord | null>((best, row) => {
      if (!best) return row;
      return (row.last_finished_at ?? 0) > (best.last_finished_at ?? 0) ? row : best;
    }, null);
  }, [statusQ.data?.items]);

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSelected(null);
    onChange({
      ...search,
      game_id: form.game_id || undefined,
      server_id: form.server_id || undefined,
      q: form.q || undefined,
      cursor: undefined,
      status_cursor: undefined,
      run_cursor: undefined,
      watch_cursor: undefined,
      item_sort: search.item_sort,
      watch_sort: search.watch_sort,
    });
  }

  function reset() {
    setForm({});
    setGameQuery("");
    setServerQuery("");
    setSelected(null);
    onChange({ tab });
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">
        Lua 采集已停用
      </div>

      <form
        aria-label="市场分析过滤器"
        onSubmit={apply}
        className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 lg:grid-cols-[1.2fr_1.4fr_2fr_auto_auto]"
      >
        <Field label="游戏">
          <MarketComboBox
            value={form.game_id ?? ""}
            displayValue={selectedGameLabel}
            placeholder="输入游戏名或 ID"
            loading={gameOptionsQ.isFetching}
            options={gameComboOptions}
            onSearchChange={setGameQuery}
            onChange={(value) => {
              setForm({ ...form, game_id: value, server_id: "" });
              setGameQuery(value);
              setServerQuery("");
            }}
          />
        </Field>
        <Field label="区服">
          <MarketComboBox
            value={form.server_id ?? ""}
            displayValue={selectedServerLabel}
            placeholder={form.game_id ? "输入区服名或 ID" : "先选游戏"}
            loading={serverOptionsQ.isFetching}
            disabled={!form.game_id}
            options={serverComboOptions}
            onSearchChange={setServerQuery}
            onChange={(value) => {
              setForm({ ...form, server_id: value });
              setServerQuery(value);
            }}
          />
        </Field>
        <Field label="商品名 / 商品 ID / 物品 ID">
          <Input value={form.q ?? ""} onChange={(e) => setForm({ ...form, q: e.target.value })} />
        </Field>
        <div className="flex items-end">
          <Button type="submit" size="sm" className="w-full">
            查询
          </Button>
        </div>
        <div className="flex items-end">
          <Button type="button" size="sm" variant="outline" className="w-full" onClick={reset}>
            重置
          </Button>
        </div>
      </form>

      <Tabs value={tab} onValueChange={(value) => onChange({ ...search, tab: value as MarketTab })}>
        <TabsList className="flex h-auto flex-wrap justify-start">
          {TAB_LABELS.map((item) => (
            <TabsTrigger key={item.value} value={item.value}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            statusQ={statusQ}
            latestStatus={latestStatus}
            collector={collector}
            setCollector={setCollector}
            canWrite={canWrite}
            selectedAgentID={selectedAgentID}
            agentOptions={agentOptions}
            selectedAgent={selectedAgent}
            search={search}
            collectMutationPending={collectMutation.isPending}
            onCollect={() => collectMutation.mutate(collectionRequest(selectedAgentID, search, collector))}
          />
        </TabsContent>

        <TabsContent value="baseline">
          <BaselineTab
            itemsQ={itemsQ}
            selected={selected}
            setSelected={setSelected}
            historyQ={historyQ}
            historyMetric={historyMetric}
            setHistoryMetric={setHistoryMetric}
            search={search}
            onChange={onChange}
            itemLimit={itemLimit}
          />
        </TabsContent>

        <TabsContent value="watch">
          <WatchTab
            watchQ={watchQ}
            search={search}
            onChange={onChange}
          />
        </TabsContent>

        <TabsContent value="compare">
          <CompareTab
            compareQ={compareQ}
            search={search}
            canWrite={canWrite}
            collectPending={compareCollectMutation.isPending}
            onCollectAll={() => compareCollectMutation.mutate({ onlyProblems: false })}
            onCollectAllWithRefresh={() => compareCollectMutation.mutate({ onlyProblems: false, refreshDirectory: true })}
            onCollectProblems={() => compareCollectMutation.mutate({ onlyProblems: true })}
          />
        </TabsContent>

        <TabsContent value="tasks">
          <TasksTab
            runsQ={runsQ}
            plansQ={plansQ}
            targetsQ={targetsQ}
            schedulerQ={schedulerQ}
            search={search}
            onChange={onChange}
            canWrite={canWrite}
            selectedAgentID={selectedAgentID}
            selectedAgentReady={marketAgentReady(selectedAgent)}
            planForm={planForm}
            setPlanForm={setPlanForm}
            onCreatePlan={() => {
              planMutation.mutate({
                name: planForm.name || `${search.game_id || ""} 基线`,
                enabled: true,
                game_id: search.game_id,
                target_kind: "server",
                target_json: { server_ids: planForm.server_id ? [planForm.server_id] : [] },
                mode: "full",
                pace_preset: "balanced",
                interval_seconds: Number(planForm.interval_seconds) || 86400,
              });
            }}
            onRunPlan={(id) => runPlanMutation.mutate(id)}
            runLimit={runLimit}
            onRefreshDirectory={() => directoryRefreshMutation.mutate()}
            refreshDirectoryPending={directoryRefreshMutation.isPending}
            onGenerateBaselineTargets={() => baselineGenerateMutation.mutate()}
            generateBaselinePending={baselineGenerateMutation.isPending}
          />
        </TabsContent>

        <TabsContent value="rules">
          <RulesTab
            rulesQ={rulesQ}
            canWrite={canWrite}
            search={search}
            ruleForm={ruleForm}
            setRuleForm={setRuleForm}
            onSubmit={() => {
              ruleMutation.mutate({
                game_id: search.game_id,
                server_id: search.server_id,
                category: ruleForm.category,
                item_key: ruleForm.item_key || search.q,
                rule_json: ruleForm.rule_json,
                note: ruleForm.note,
              });
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab({
  statusQ,
  latestStatus,
  collector,
  setCollector,
  canWrite,
  selectedAgentID,
  agentOptions,
  selectedAgent,
  search,
  collectMutationPending,
  onCollect,
}: {
  statusQ: ReturnType<typeof useQuery<PageResponse<MarketStatusRecord>>>;
  latestStatus: MarketStatusRecord | null;
  collector: CollectorForm;
  setCollector: (next: CollectorForm) => void;
  canWrite: boolean;
  selectedAgentID: string;
  agentOptions: MarketAgentOption[];
  selectedAgent?: MarketAgentOption;
  search: MarketSearch;
  collectMutationPending: boolean;
  onCollect: () => void;
}) {
  const agentReady = marketAgentReady(selectedAgent);
  const selectedAgentVariant: StatusVariant = agentReady ? "ok" : selectedAgent?.market_cdp_enabled && !selectedAgent.box996_installed && hasAccountCDPBinding(selectedAgent) ? "warn" : selectedAgent ? "danger" : "neutral";
  return (
    <div className="space-y-4">
      <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <SummaryTile label="覆盖区服" value={String(statusQ.data?.total ?? statusQ.data?.items.length ?? 0)} />
        <SummaryTile label="当前商品" value={formatNumber(sumStatus(statusQ.data?.items, "current_count"))} />
        <SummaryTile label="最近新增/变动" value={formatNumber(latestStatus?.last_changed_count ?? 0)} />
        <SummaryTile label="最近完成" value={formatTime(latestStatus?.last_finished_at)} />
      </section>
      <section className="space-y-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-medium">Python CDP 采集</h2>
          <StatusBadge variant={canWrite ? "ok" : "neutral"}>{canWrite ? "operator" : "viewer"}</StatusBadge>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_repeat(6,minmax(0,1fr))]">
          <Field label="账号 ID">
            <Input
              value={collector.account_id}
              onChange={(e) => setCollector({ ...collector, account_id: e.target.value })}
              disabled={!canWrite}
              inputMode="numeric"
              placeholder="可选"
            />
          </Field>
          <Field label="Windows agent">
            <select
              className="h-9 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
              value={selectedAgentID}
              onChange={(e) => setCollector({ ...collector, agent_id: e.target.value })}
              disabled={!canWrite}
            >
              {agentOptions.length === 0 ? <option value="">无在线 agent</option> : null}
              {agentOptions.map((agent) => (
                <option key={agent.agent_id} value={agent.agent_id}>
                  {agent.agent_id}{agent.hostname ? ` · ${agent.hostname}` : ""}{agentOptionSuffix(agent)}
                </option>
              ))}
            </select>
          </Field>
          <SelectField label="模式" value={collector.mode} disabled={!canWrite} onChange={(value) => setCollector({ ...collector, mode: value as CollectorForm["mode"] })} options={[["full", "全量"], ["incremental", "增量"]]} />
          <SelectField label="节奏" value={collector.pace_preset} disabled={!canWrite} onChange={(value) => setCollector({ ...collector, pace_preset: value as CollectorForm["pace_preset"] })} options={[["balanced", "balanced"], ["safe", "safe"], ["fast", "fast"]]} />
          <Field label="页数">
            <Input value={collector.limit_pages} onChange={(e) => setCollector({ ...collector, limit_pages: e.target.value })} disabled={!canWrite} />
          </Field>
          <Field label="CDP 端口">
            <Input
              value={collector.port}
              onChange={(e) => setCollector({ ...collector, port: e.target.value })}
              disabled={!canWrite}
              inputMode="numeric"
              placeholder="可选"
            />
          </Field>
          <SelectField label="单价排序" value={collector.unit_price} disabled={!canWrite} onChange={(value) => setCollector({ ...collector, unit_price: value as CollectorForm["unit_price"] })} options={[["", "无"], ["asc", "升序"], ["desc", "降序"]]} />
          <SelectField label="总价排序" value={collector.order_price} disabled={!canWrite} onChange={(value) => setCollector({ ...collector, order_price: value as CollectorForm["order_price"] })} options={[["", "无"], ["asc", "升序"], ["desc", "降序"]]} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <StatusBadge variant={selectedAgentVariant}>
            {selectedAgent ? agentCapabilityText(selectedAgent) : "无在线 agent"}
          </StatusBadge>
          {selectedAgent?.box996_exe_path ? <span className="font-mono">{selectedAgent.box996_exe_path}</span> : null}
          {selectedAgent && !selectedAgent.box996_installed && hasAccountCDPBinding(selectedAgent) ? <span className="font-mono">{accountCDPDetail(selectedAgent)}</span> : null}
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr_1fr_auto]">
          <Field label="关键词">
            <Input value={collector.keyword} onChange={(e) => setCollector({ ...collector, keyword: e.target.value })} disabled={!canWrite} />
          </Field>
          <Field label="最低价">
            <Input value={collector.min_price} onChange={(e) => setCollector({ ...collector, min_price: e.target.value })} disabled={!canWrite} />
          </Field>
          <Field label="最高价">
            <Input value={collector.max_price} onChange={(e) => setCollector({ ...collector, max_price: e.target.value })} disabled={!canWrite} />
          </Field>
          <div className="flex items-end">
            <Button type="button" size="sm" disabled={!canStartCDP(canWrite, selectedAgentID, collector.account_id, search, agentReady) || collectMutationPending} onClick={onCollect}>
              开始采集
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function BaselineTab({
  itemsQ,
  selected,
  setSelected,
  historyQ,
  historyMetric,
  setHistoryMetric,
  search,
  onChange,
  itemLimit,
}: {
  itemsQ: ReturnType<typeof useQuery<PageResponse<MarketItemRecord>>>;
  selected: MarketItemRecord | null;
  setSelected: (row: MarketItemRecord | null) => void;
  historyQ: ReturnType<typeof useQuery<{ ok: boolean; items: MarketItemHistoryRecord[] }>>;
  historyMetric: HistoryMetric;
  setHistoryMetric: (value: HistoryMetric) => void;
  search: MarketSearch;
  onChange: (next: MarketSearch) => void;
  itemLimit: number;
}) {
  return (
    <div className="space-y-4">
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(420px,1fr)]">
        <div className="space-y-2">
          <h2 className="text-base font-medium">当前商品</h2>
          <QueryView query={itemsQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无商品行情" description="开始采集后会显示完整采集和关键词过滤采集得到的商品。" />}>
            {(data) => (
              <>
                <Table dense>
                  <TableHeader>
                    <TableRow>
                      <TableHead>商品</TableHead>
                      <SortableTableHead
                        active={search.item_sort === "min_price_asc" || search.item_sort === "min_price_desc"}
                        direction={search.item_sort === "min_price_desc" ? "desc" : "asc"}
                        label="最低价"
                        onClick={() => onChange({
                          ...search,
                          item_sort: search.item_sort === "min_price_desc" ? "min_price_asc" : "min_price_desc",
                          cursor: undefined,
                        })}
                      />
                      <TableHead>中位/均价</TableHead>
                      <TableHead>挂单</TableHead>
                      <TableHead>压价</TableHead>
                      <TableHead>新鲜度</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((row) => {
                      const subtitle = marketItemSubtitle(row);
                      return (
                        <TableRow key={`${row.game_id}:${row.canonical_server_id}:${row.item_key}`} data-state={selected?.item_key === row.item_key ? "selected" : undefined}>
                          <TableCell>
                            <div className="font-medium">{row.name || "-"}</div>
                            <div className="font-mono text-[11px] text-[hsl(var(--muted-foreground))]">{subtitle}</div>
                          </TableCell>
                          <TableCell>
                            <div>{formatPrice(row.effective_min_price ?? row.min_price)}</div>
                            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">原始 {formatPrice(row.raw_min_price ?? row.min_price)}</div>
                            {row.data_source === "history" ? <StatusBadge variant="neutral" className="mt-1">历史参考</StatusBadge> : null}
                          </TableCell>
                          <TableCell>
                            <div>中位 {formatPrice(row.reference_price ?? row.median_price)}</div>
                            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">均价 {formatPrice(row.avg_price)}</div>
                          </TableCell>
                          <TableCell>
                            <div>{formatNumber(row.listing_count)}</div>
                            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">数量 {formatQuantity(row.total_quantity)}</div>
                          </TableCell>
                          <TableCell><PriceSignalBadge row={row} /></TableCell>
                          <TableCell>{formatTime(row.last_seen_at)}</TableCell>
                          <TableCell>
                            <Button type="button" variant="outline" size="sm" onClick={() => setSelected(row)}>查看走势</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Pager
                  data={data}
                  onPrev={() => onChange({ ...search, cursor: Math.max(0, (search.cursor ?? 0) - itemLimit) })}
                  onNext={() => onChange({ ...search, cursor: data.next_cursor })}
                />
              </>
            )}
          </QueryView>
        </div>
        <HistoryPanel selected={selected} historyQ={historyQ} metric={historyMetric} onMetricChange={setHistoryMetric} />
      </section>
    </div>
  );
}

function WatchTab({
  watchQ,
  search,
  onChange,
}: {
  watchQ: ReturnType<typeof useQuery<PageResponse<MarketWatchItemRecord>>>;
  search: MarketSearch;
  onChange: (next: MarketSearch) => void;
}) {
  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center justify-between gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
        <div>
          <h2 className="text-sm font-medium">价格监控</h2>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            监控清单来自 market_watch_item，daemon 默认每 30 分钟按归一区服和物品抓取交易行价格。
          </p>
        </div>
        {search.game_id ? (
          <Button asChild size="sm" variant="outline">
            <Link
              to="/games/$gameId"
              params={{ gameId: search.game_id }}
              search={{ tab: "materials" }}
            >
              查看物品清单
            </Link>
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled>
            查看物品清单
          </Button>
        )}
      </section>
      <QueryView query={watchQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无价格监控" description="当前筛选条件下没有启用的市场监控目标。" />}>
        {(data) => (
          <>
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>物品</TableHead>
                  <TableHead>区服</TableHead>
                  <TableHead>间隔</TableHead>
                  <TableHead>最近 full</TableHead>
                  <SortableTableHead
                    active={search.watch_sort === "min_price_asc" || search.watch_sort === "min_price_desc"}
                    direction={search.watch_sort === "min_price_desc" ? "desc" : "asc"}
                    label="最低价"
                    onClick={() => onChange({
                      ...search,
                      watch_sort: search.watch_sort === "min_price_desc" ? "min_price_asc" : "min_price_desc",
                      watch_cursor: undefined,
                    })}
                  />
                  <TableHead>中位</TableHead>
                  <TableHead>挂单</TableHead>
                  <TableHead>压价</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="font-medium">{row.item_name}</div>
                      <div className="font-mono text-[11px] text-[hsl(var(--muted-foreground))]">{row.material_item_id || row.item_key}</div>
                    </TableCell>
                    <TableCell>{row.server_name || row.canonical_server_id || "-"}</TableCell>
                    <TableCell>{formatDuration(row.interval_seconds)}</TableCell>
                    <TableCell>{formatTime(row.last_full_at)}</TableCell>
                    <TableCell>
                      <div>{formatPrice(row.min_price_value || row.reference_price)}</div>
                      {row.data_source === "history" ? <StatusBadge variant="neutral" className="mt-1">历史参考</StatusBadge> : null}
                    </TableCell>
                    <TableCell>{formatPrice(row.median_price)}</TableCell>
                    <TableCell>{formatNumber(row.listing_count)}</TableCell>
                    <TableCell>{row.suspect_type ? `${signalText(row.suspect_type)} ${row.suspect_score ?? ""}` : "无"}</TableCell>
                    <TableCell>
                      <StatusBadge variant={row.enabled ? watchStatusVariant(row.last_status) : "neutral"}>
                        {row.enabled ? watchStatusText(row.last_status) : "停用"}
                      </StatusBadge>
                      {row.last_error ? <div className="mt-1 max-w-[220px] truncate text-[11px] text-[hsl(var(--muted-foreground))]">{row.last_error}</div> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager data={data} onPrev={() => onChange({ ...search, watch_cursor: Math.max(0, (search.watch_cursor ?? 0) - 50) })} onNext={() => onChange({ ...search, watch_cursor: data.next_cursor })} />
          </>
        )}
      </QueryView>
    </div>
  );
}

function SortableTableHead({
  active,
  direction,
  label,
  onClick,
}: {
  active: boolean;
  direction: "asc" | "desc";
  label: ReactNode;
  onClick: () => void;
}) {
  return (
    <TableHead aria-sort={active ? (direction === "desc" ? "descending" : "ascending") : "none"}>
      <button
        type="button"
        className="inline-flex h-7 items-center gap-1 rounded px-1 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
        onClick={onClick}
      >
        <span>{label}</span>
        {active ? (
          <ArrowDown className={cn("h-3.5 w-3.5", direction === "asc" && "rotate-180")} aria-hidden="true" />
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    </TableHead>
  );
}

function CompareTab({
  compareQ,
  search,
  canWrite,
  collectPending,
  onCollectAll,
  onCollectAllWithRefresh,
  onCollectProblems,
}: {
  compareQ: ReturnType<typeof useQuery<{ ok: boolean; items: OpenDayCompareRecord[] }>>;
  search: MarketSearch;
  canWrite: boolean;
  collectPending: boolean;
  onCollectAll: () => void;
  onCollectAllWithRefresh: () => void;
  onCollectProblems: () => void;
}) {
  if (!search.game_id || !search.server_id || !search.q) {
    return <Empty title="缺少比价条件" description="需要游戏 ID、区服 ID 和物品名。" />;
  }
  return (
    <div className="space-y-3">
      <section className="flex flex-wrap items-center justify-between gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">同开区采集</h2>
          <div className="mt-1 font-mono text-xs text-[hsl(var(--muted-foreground))]">
            {search.game_id} / {search.server_id} / {search.q}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" disabled={!canWrite || collectPending} onClick={onCollectProblems}>
            只补抓问题区
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={!canWrite || collectPending} onClick={onCollectAll}>
            按当前列表抓
          </Button>
          <Button type="button" size="sm" disabled={!canWrite || collectPending} onClick={onCollectAllWithRefresh}>
            同步区服并抓同开区
          </Button>
        </div>
      </section>
      <QueryView query={compareQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无同开区数据" />}>
        {(data) => (
          <Table dense>
            <TableHeader>
              <TableRow>
                <TableHead>区服</TableHead>
                <TableHead>库存</TableHead>
                <TableHead>参考最低</TableHead>
                <TableHead>原始最低</TableHead>
                <TableHead>挂单</TableHead>
                <TableHead>价差</TableHead>
                <TableHead>最近采集</TableHead>
                <TableHead>补抓</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => (
                <TableRow key={row.server_id}>
                  <TableCell>
                    <div className="font-medium">{row.server_name || row.server_id}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">第 {row.open_batch} 区 · rank {row.rank_by_effective_min || "-"}</div>
                  </TableCell>
                  <TableCell><StockBadge state={row.stock_state} /></TableCell>
                  <TableCell>{row.has_listing ? formatPrice(row.effective_min_price) : "-"}</TableCell>
                  <TableCell>{row.has_listing ? formatPrice(row.raw_min_price) : "-"}</TableCell>
                  <TableCell>{formatNumber(row.listing_count)}</TableCell>
                  <TableCell>{row.has_listing ? formatPrice(row.delta_to_cheapest) : "-"}</TableCell>
                  <TableCell>{formatTime(row.last_seen_at || row.last_full_at)}</TableCell>
                  <TableCell>{row.needs_collect ? <StatusBadge variant="warn">需要</StatusBadge> : <StatusBadge variant="ok">正常</StatusBadge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </QueryView>
    </div>
  );
}

function TasksTab({
  runsQ,
  plansQ,
  targetsQ,
  schedulerQ,
  search,
  onChange,
  canWrite,
  selectedAgentID,
  selectedAgentReady,
  planForm,
  setPlanForm,
  onCreatePlan,
  onRunPlan,
  runLimit,
  onRefreshDirectory,
  refreshDirectoryPending,
  onGenerateBaselineTargets,
  generateBaselinePending,
}: {
  runsQ: ReturnType<typeof useQuery<PageResponse<MarketRunRecord>>>;
  plansQ: ReturnType<typeof useQuery<PageResponse<MarketPlanRecord>>>;
  targetsQ: ReturnType<typeof useQuery<PageResponse<MarketTargetRecord>>>;
  schedulerQ: ReturnType<typeof useQuery<MarketSchedulerResponse>>;
  search: MarketSearch;
  onChange: (next: MarketSearch) => void;
  canWrite: boolean;
  selectedAgentID: string;
  selectedAgentReady: boolean;
  planForm: { name: string; interval_seconds: string; server_id: string };
  setPlanForm: (next: { name: string; interval_seconds: string; server_id: string }) => void;
  onCreatePlan: () => void;
  onRunPlan: (id: number) => void;
  runLimit: number;
  onRefreshDirectory: () => void;
  refreshDirectoryPending: boolean;
  onGenerateBaselineTargets: () => void;
  generateBaselinePending: boolean;
}) {
  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center justify-between gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">批量任务</h2>
          <div className="mt-1 font-mono text-xs text-[hsl(var(--muted-foreground))]">{search.game_id || "未选择游戏"}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" disabled={!canWrite || !search.game_id || !selectedAgentID || !selectedAgentReady || refreshDirectoryPending} onClick={onRefreshDirectory}>
            刷新区服目录
          </Button>
          <Button type="button" size="sm" disabled={!canWrite || !search.game_id || generateBaselinePending} onClick={onGenerateBaselineTargets}>
            生成每日基线目标
          </Button>
        </div>
      </section>
      {canWrite ? (
        <section className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 lg:grid-cols-[2fr_1fr_1fr_auto]">
          <Field label="计划名"><Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} /></Field>
          <Field label="区服 ID"><Input value={planForm.server_id} onChange={(e) => setPlanForm({ ...planForm, server_id: e.target.value })} /></Field>
          <Field label="间隔秒"><Input value={planForm.interval_seconds} onChange={(e) => setPlanForm({ ...planForm, interval_seconds: e.target.value })} /></Field>
          <div className="flex items-end"><Button type="button" size="sm" disabled={!search.game_id} onClick={onCreatePlan}>保存计划</Button></div>
        </section>
      ) : null}
      <section className="space-y-2">
        <h2 className="text-base font-medium">采集计划</h2>
        <QueryView query={plansQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无采集计划" />}>
          {(data) => (
            <>
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>名称</TableHead>
                    <TableHead>游戏</TableHead>
                    <TableHead>模式</TableHead>
                    <TableHead>间隔</TableHead>
                    <TableHead>策略</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name || `plan #${row.id}`}</TableCell>
                      <TableCell>{row.game_id}</TableCell>
                      <TableCell>{row.mode}</TableCell>
                      <TableCell>{row.interval_seconds}s</TableCell>
                      <TableCell>{row.agent_strategy}</TableCell>
                      <TableCell>
                        <Button type="button" size="sm" variant="outline" disabled={!canWrite || !selectedAgentID || !selectedAgentReady} onClick={() => onRunPlan(row.id)}>运行</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pager data={data} onPrev={() => onChange({ ...search, plan_cursor: Math.max(0, (search.plan_cursor ?? 0) - 50) })} onNext={() => onChange({ ...search, plan_cursor: data.next_cursor })} />
            </>
          )}
        </QueryView>
      </section>
      <section className="space-y-2">
        <h2 className="text-base font-medium">采集目标</h2>
        <QueryView query={targetsQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无采集目标" />}>
          {(data) => (
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>目标</TableHead>
                  <TableHead>scope</TableHead>
                  <TableHead>最近 run</TableHead>
                  <TableHead>失败</TableHead>
                  <TableHead>下次</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.server_id || "全区"} · {row.keyword || row.item_key || "基线"}</TableCell>
                    <TableCell>{row.baseline_scope ? "基线" : "过滤"} · {row.unit_price || row.order_price || "-"}</TableCell>
                    <TableCell>#{row.last_run_id || "-"} {statusText(row.last_status)}</TableCell>
                    <TableCell>{formatNumber(row.failure_count)}</TableCell>
                    <TableCell>{formatTime(row.next_due_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryView>
      </section>
      <section className="space-y-2">
        <h2 className="text-base font-medium">最近 run</h2>
        <QueryView query={runsQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无采集 run" />}>
          {(data) => (
            <>
              <RunsTable rows={data.items} />
              <Pager data={data} onPrev={() => onChange({ ...search, run_cursor: Math.max(0, (search.run_cursor ?? 0) - runLimit) })} onNext={() => onChange({ ...search, run_cursor: data.next_cursor })} />
            </>
          )}
        </QueryView>
      </section>
      <section className="space-y-2">
        <h2 className="text-base font-medium">调度状态</h2>
        <QueryView query={schedulerQ} isEmpty={(data) => data.targets.length === 0 && data.leases.length === 0 && (data.capabilities?.length ?? 0) === 0} empty={<Empty title="暂无调度状态" />}>
          {(data) => (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <SummaryTile label="活跃 lease" value={formatNumber(data.leases.length)} />
                <SummaryTile label="待调度目标" value={formatNumber(data.targets.length)} />
                <SummaryTile label="agent 覆盖" value={formatNumber(data.capabilities?.length ?? 0)} />
              </div>
              {(data.capabilities?.length ?? 0) > 0 ? <CapabilitiesTable rows={data.capabilities ?? []} /> : null}
            </div>
          )}
        </QueryView>
      </section>
    </div>
  );
}

function RulesTab({
  rulesQ,
  canWrite,
  search,
  ruleForm,
  setRuleForm,
  onSubmit,
}: {
  rulesQ: ReturnType<typeof useQuery<PageResponse<MarketPriceRuleRecord>>>;
  canWrite: boolean;
  search: MarketSearch;
  ruleForm: { category: string; item_key: string; rule_json: string; note: string };
  setRuleForm: (next: { category: string; item_key: string; rule_json: string; note: string }) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-4">
      {canWrite ? (
        <section className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 lg:grid-cols-[1fr_1fr_2fr_1fr_auto]">
          <Field label="分类"><Input value={ruleForm.category} onChange={(e) => setRuleForm({ ...ruleForm, category: e.target.value })} /></Field>
          <Field label="物品 key"><Input value={ruleForm.item_key} onChange={(e) => setRuleForm({ ...ruleForm, item_key: e.target.value })} placeholder={search.q || ""} /></Field>
          <Field label="规则 JSON"><Input value={ruleForm.rule_json} onChange={(e) => setRuleForm({ ...ruleForm, rule_json: e.target.value })} /></Field>
          <Field label="备注"><Input value={ruleForm.note} onChange={(e) => setRuleForm({ ...ruleForm, note: e.target.value })} /></Field>
          <div className="flex items-end"><Button type="button" size="sm" disabled={!search.game_id && !ruleForm.item_key} onClick={onSubmit}>保存</Button></div>
        </section>
      ) : null}
      <QueryView query={rulesQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无价格规则" />}>
        {(data) => (
          <Table dense>
            <TableHeader>
              <TableRow>
                <TableHead>scope</TableHead>
                <TableHead>规则</TableHead>
                <TableHead>备注</TableHead>
                <TableHead>更新人</TableHead>
                <TableHead>更新时间</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row, index) => (
                <TableRow key={`${row.game_id}:${row.canonical_server_id}:${row.category}:${row.item_key}:${index}`}>
                  <TableCell>{row.game_id || "global"} / {row.canonical_server_id || "*"} / {row.category || "*"} / {row.item_key || "*"}</TableCell>
                  <TableCell className="max-w-[420px] truncate font-mono text-xs">{row.rule_json}</TableCell>
                  <TableCell>{row.note || "-"}</TableCell>
                  <TableCell>{row.updated_by || "-"}</TableCell>
                  <TableCell>{formatTime(row.updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </QueryView>
    </div>
  );
}

function HistoryPanel({
  selected,
  historyQ,
  metric,
  onMetricChange,
}: {
  selected: MarketItemRecord | null;
  historyQ: ReturnType<typeof useQuery<{ ok: boolean; items: MarketItemHistoryRecord[] }>>;
  metric: HistoryMetric;
  onMetricChange: (value: HistoryMetric) => void;
}) {
  return (
    <div className="space-y-2">
      <h2 className="text-base font-medium">单价走势</h2>
      {!selected ? (
        <Empty title="未选择商品" />
      ) : (
        <QueryView query={historyQ} isEmpty={(data) => data.items.length === 0} empty={<Empty title="暂无历史版本" />}>
          {(data) => (
            <div className="space-y-3">
              <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium">{selected.name || selected.item_key}</div>
                  <MetricSelector value={metric} onChange={onMetricChange} />
                </div>
                <PriceSparkline items={data.items} metric={metric} />
              </div>
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>日期</TableHead>
                    <TableHead>原始最低</TableHead>
                    <TableHead>参考最低</TableHead>
                    <TableHead>均单价</TableHead>
                    <TableHead>中位</TableHead>
                    <TableHead>挂单</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((row) => (
                    <TableRow key={row.day}>
                      <TableCell>{row.day}</TableCell>
                      <TableCell>{formatPrice(row.daily_raw_min_price ?? row.daily_min_price)}</TableCell>
                      <TableCell>{formatPrice(row.daily_effective_min_price ?? row.latest_min_price)}</TableCell>
                      <TableCell>{formatPrice(row.latest_avg_price)}</TableCell>
                      <TableCell>{formatPrice(row.latest_median_price)}</TableCell>
                      <TableCell>{formatNumber(row.latest_listing_count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </QueryView>
      )}
    </div>
  );
}

function RunsTable({ rows }: { rows: MarketRunRecord[] }) {
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>run</TableHead>
          <TableHead>scope</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>条目</TableHead>
          <TableHead>变动</TableHead>
          <TableHead>agent</TableHead>
          <TableHead>完成时间</TableHead>
          <TableHead>错误</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>#{row.id}</TableCell>
            <TableCell>{row.game_id} / {row.server_id || row.canonical_server_id} / {row.mode || "-"}</TableCell>
            <TableCell><StatusBadge variant={statusVariant(row.status)}>{statusText(row.status)}</StatusBadge></TableCell>
            <TableCell>{formatNumber(row.item_count)}</TableCell>
            <TableCell>{formatNumber(row.changed_count)}</TableCell>
            <TableCell>{row.agent_id || "-"}</TableCell>
            <TableCell>{formatTime(row.finished_at || row.started_at)}</TableCell>
            <TableCell className="max-w-[260px] truncate">{row.error || "-"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CapabilitiesTable({ rows }: { rows: MarketAgentCapability[] }) {
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>agent</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>CDP</TableHead>
          <TableHead>996box</TableHead>
          <TableHead>游戏</TableHead>
          <TableHead>区服</TableHead>
          <TableHead>角色</TableHead>
          <TableHead>来源</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.agent_id}:${row.game_id || "*"}:${row.canonical_server_id || row.server_id || "*"}:${index}`}>
            <TableCell>
              <div className="font-medium">{row.agent_id}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{row.hostname || row.peer || "-"}</div>
            </TableCell>
            <TableCell>
              <StatusBadge variant={capabilityVariant(row)}>
                {row.busy ? "忙碌" : row.authenticated ? statusText(row.state) : "未认证"}
              </StatusBadge>
            </TableCell>
            <TableCell>
              <StatusBadge variant={row.market_cdp_enabled ? "ok" : "neutral"}>
                {row.market_cdp_enabled ? "开启" : "关闭"}
              </StatusBadge>
              {cdpPortLabel(row) ? (
                <div className="mt-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                  {cdpPortLabel(row)}
                </div>
              ) : null}
            </TableCell>
            <TableCell>
              <StatusBadge variant={box996StatusVariant(row)}>
                {box996StatusLabel(row)}
              </StatusBadge>
              {box996DetailLine(row) ? <div className="mt-1 max-w-[220px] truncate font-mono text-[10px] text-[hsl(var(--muted-foreground))]">{box996DetailLine(row)}</div> : null}
            </TableCell>
            <TableCell>{row.game_id || "-"}</TableCell>
            <TableCell>
              <div>{marketServerLabel(row.server_name, row.canonical_server_id || row.server_id) || "-"}</div>
              <div className="font-mono text-[11px] text-[hsl(var(--muted-foreground))]">{row.canonical_server_id || row.server_id || "-"}</div>
            </TableCell>
            <TableCell>{formatNumber(row.role_count ?? 0)}</TableCell>
            <TableCell className="max-w-[260px] truncate">{capabilitySourceText(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function capabilityVariant(row: MarketAgentCapability): StatusVariant {
  if (!row.authenticated) return "danger";
  if (row.busy) return "warn";
  if (!row.market_cdp_enabled) return "danger";
  if (!row.box996_installed) return hasAccountCDPBinding(row) ? "warn" : "danger";
  if (!row.game_id) return "neutral";
  return row.state === "" || row.state === "ready" ? "ok" : statusVariant(row.state);
}

function watchStatusVariant(status?: string): StatusVariant {
  switch (status) {
    case "success":
    case "collected":
      return "ok";
    case "uncovered":
    case "agent_unavailable":
      return "warn";
    default:
      return statusVariant(status);
  }
}

function watchStatusText(status?: string): string {
  switch (status) {
    case "uncovered":
      return "无覆盖";
    case "collected":
      return "已采集";
    case "":
    case undefined:
      return "待调度";
    default:
      return statusText(status);
  }
}

function marketAgentOptions(caps: MarketAgentCapability[] = [], onlineAgents: DashboardAgentInfo[] = []): MarketAgentOption[] {
  const byID = new Map<string, MarketAgentOption>();
  for (const cap of caps) {
    if (!cap.agent_id) continue;
    const current = byID.get(cap.agent_id);
    const option: MarketAgentOption = {
      agent_id: cap.agent_id,
      hostname: cap.hostname,
      state: cap.state,
      authenticated: cap.authenticated,
      busy: cap.busy,
      market_cdp_enabled: cap.market_cdp_enabled,
      box996_installed: cap.box996_installed,
      box996_exe_path: cap.box996_exe_path,
      cdp_port: cap.cdp_port,
      cdp_ports: cap.cdp_ports,
      cdp_blocked_reason: cap.cdp_blocked_reason,
      account_cdp_binding_count: cap.account_cdp_binding_count,
      account_cdp_box_exe_paths: cap.account_cdp_box_exe_paths,
      account_cdp_ports: cap.account_cdp_ports,
    };
    if (!current || (marketAgentReady(option) && !marketAgentReady(current))) {
      byID.set(cap.agent_id, option);
    }
  }
  for (const agent of onlineAgents) {
    if (!agent.agent_id || byID.has(agent.agent_id)) continue;
    byID.set(agent.agent_id, {
      agent_id: agent.agent_id,
      hostname: agent.hostname,
      state: agent.state,
      authenticated: true,
      market_cdp_enabled: false,
      box996_installed: false,
      cdp_blocked_reason: "996box_missing",
    });
  }
  return Array.from(byID.values()).sort((a, b) => a.agent_id.localeCompare(b.agent_id));
}

function marketAgentReady(agent?: MarketAgentOption): boolean {
  if (!agent) return false;
  if (agent.authenticated === false || agent.busy) return false;
  if (agent.state && agent.state !== "ready" && agent.state !== "online") return false;
  return agent.market_cdp_enabled === true && agent.box996_installed === true;
}

function cdpPortLabel(agent: Pick<MarketAgentCapability, "cdp_port" | "cdp_ports">): string {
  const ports = new Set<number>();
  if (agent.cdp_port && agent.cdp_port > 0) ports.add(agent.cdp_port);
  for (const port of agent.cdp_ports ?? []) {
    if (port > 0) ports.add(port);
  }
  const sorted = Array.from(ports).sort((a, b) => a - b);
  return sorted.length > 0 ? `端口 ${sorted.join(",")}` : "";
}

function accountCDPBindingCount(agent: Pick<MarketAgentCapability, "account_cdp_binding_count" | "account_cdp_box_exe_paths">): number {
  return Math.max(agent.account_cdp_binding_count ?? 0, agent.account_cdp_box_exe_paths?.length ?? 0);
}

function hasAccountCDPBinding(agent: Pick<MarketAgentCapability, "account_cdp_binding_count" | "account_cdp_box_exe_paths" | "account_cdp_ports">): boolean {
  return accountCDPBindingCount(agent) > 0 || (agent.account_cdp_ports?.length ?? 0) > 0;
}

function accountCDPDetail(agent: Pick<MarketAgentCapability, "account_cdp_binding_count" | "account_cdp_box_exe_paths" | "account_cdp_ports">): string {
  const parts: string[] = [];
  const count = accountCDPBindingCount(agent);
  if (count > 0) parts.push(`${count} 个账号`);
  const ports = Array.from(new Set((agent.account_cdp_ports ?? []).filter((port) => port > 0))).sort((a, b) => a - b);
  if (ports.length > 0) parts.push(`端口 ${ports.join(",")}`);
  const firstPath = agent.account_cdp_box_exe_paths?.find((path) => path.trim() !== "");
  if (firstPath) parts.push(firstPath);
  return parts.join(" · ");
}

function box996StatusVariant(row: MarketAgentCapability): StatusVariant {
  if (row.box996_installed) return "ok";
  return hasAccountCDPBinding(row) ? "warn" : "danger";
}

function box996StatusLabel(row: MarketAgentCapability): string {
  if (row.box996_installed) return "已检测";
  return hasAccountCDPBinding(row) ? "账号路径" : "缺少";
}

function box996DetailLine(row: MarketAgentCapability): string {
  if (row.box996_installed) return row.box996_exe_path || "";
  return accountCDPDetail(row);
}

function capabilitySourceText(row: MarketAgentCapability): string {
  if (!row.box996_installed && hasAccountCDPBinding(row) && row.cdp_blocked_reason === "996box_missing") {
    const detail = accountCDPDetail(row);
    return detail ? `账号路径可用（需填账号 ID） · ${detail}` : "账号路径可用（需填账号 ID）";
  }
  return statusText(row.cdp_blocked_reason) || row.reason || row.peer_host || row.peer || "-";
}

function agentOptionSuffix(agent: MarketAgentOption): string {
  if (marketAgentReady(agent)) return "";
  return ` · ${agentCapabilityText(agent)}`;
}

function agentCapabilityText(agent: MarketAgentOption): string {
  if (agent.authenticated === false) return "未认证";
  if (agent.busy) return "忙碌";
  if (!agent.market_cdp_enabled) return "CDP 开关关闭";
  if (!agent.box996_installed) return hasAccountCDPBinding(agent) ? "账号路径可用（需填账号 ID）" : "缺少 996box.exe";
  if (agent.state && agent.state !== "ready" && agent.state !== "online") return statusText(agent.state);
  return "可采集";
}

function queryString(fields: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, value);
  });
  const text = params.toString();
  return text ? `?${text}` : "";
}

function canStartCDP(
  canWrite: boolean,
  agentID: string,
  accountID: string,
  search: MarketSearch,
  agentReady = true,
): boolean {
  const hasAccountBinding = Boolean(accountID.trim());
  return Boolean(
    canWrite &&
      (hasAccountBinding || (agentID && agentReady)) &&
      search.game_id &&
      search.server_id,
  );
}

function collectionRequest(agentID: string, search: MarketSearch, form: CollectorForm): MarketCollectionRequest {
  const limit = Number(form.limit_pages);
  const accountID = Number(form.account_id);
  const port = Number(form.port);
  return {
    source: "python_cdp",
    account_id: Number.isFinite(accountID) && accountID > 0 ? Math.trunc(accountID) : undefined,
    agent_id: Number.isFinite(accountID) && accountID > 0 ? "" : agentID,
    game_id: search.game_id ?? "",
    server_id: search.server_id ?? "",
    category: "equipment",
    mode: form.mode,
    pace_preset: form.pace_preset,
    limit_pages: Number.isFinite(limit) && limit >= 0 ? Math.trunc(limit) : undefined,
    keyword: form.keyword || search.q || undefined,
    equip_type_id: "7",
    unit_price: form.unit_price || undefined,
    order_price: form.order_price || undefined,
    min_price: form.min_price || undefined,
    max_price: form.max_price || undefined,
    port: Number.isFinite(port) && port > 0 ? Math.trunc(port) : undefined,
  };
}

function PriceSignalBadge({ row }: { row: MarketItemRecord }) {
  if (!row.suspect_type || !row.suspect_score) {
    return <span className="text-[hsl(var(--muted-foreground))]">无</span>;
  }
  return (
    <div>
      <StatusBadge variant={row.suspect_score >= 70 ? "danger" : "warn"}>
        {signalText(row.suspect_type)} {row.suspect_score}
      </StatusBadge>
      {row.suspect_reasons ? <div className="mt-1 max-w-[220px] text-[11px] text-[hsl(var(--muted-foreground))]">{row.suspect_reasons}</div> : null}
    </div>
  );
}

function signalText(type: string): string {
  switch (type) {
    case "low_price_bait":
      return "小单压价";
    case "low_price_wall":
      return "压价墙";
    case "repeat_seller_pressure":
      return "反复压价";
    case "price_anomaly":
      return "异常";
    default:
      return type;
  }
}

function StockBadge({ state }: { state: string }) {
  switch (state) {
    case "in_stock":
      return <StatusBadge variant="ok">有货</StatusBadge>;
    case "low_stock":
      return <StatusBadge variant="warn">低库存</StatusBadge>;
    case "stale":
      return <StatusBadge variant="warn">过期</StatusBadge>;
    case "out_of_stock":
      return <StatusBadge variant="danger">缺货</StatusBadge>;
    default:
      return <StatusBadge variant="neutral">{state || "未知"}</StatusBadge>;
  }
}

function marketItemSubtitle(row: MarketItemRecord): string {
  const parts = [marketItemIdentifier(row), marketServerLabel(row.canonical_server_name, row.canonical_server_id)].filter(Boolean);
  return parts.join(" · ") || "-";
}

function marketItemIdentifier(row: MarketItemRecord): string {
  const itemID = String(row.item_id || "").trim();
  if (itemID) return itemID;
  const itemKey = String(row.item_key || "").trim();
  const name = String(row.name || "").trim();
  return itemKey && itemKey !== name ? itemKey : "";
}

function marketServerLabel(name: string | undefined, fallbackID: string | undefined): string {
  const normalized = normalizeMarketServerName(name);
  return normalized || String(fallbackID || "").trim();
}

function normalizeMarketServerName(value: string | undefined): string {
  const text = String(value || "").trim();
  if (!text) return "";
  const parts = text.split(/\s*[·｜|]\s*/).filter(Boolean);
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index].trim();
    if (isMarketServerName(part)) return part;
  }
  return text;
}

function isMarketServerName(value: string): boolean {
  return /\d{1,2}月\d{1,2}日\d{1,3}区/.test(value) || /\d{1,2}[-/]\d{1,2}.*区/.test(value);
}

function statusVariant(status?: string): StatusVariant {
  switch (status) {
    case "success":
    case "collected":
      return "ok";
    case "dispatch_failed":
    case "agent_unavailable":
    case "uv_missing":
    case "996box_missing":
    case "market_cdp_disabled":
    case "python_env_failed":
    case "cdp_unavailable":
    case "page_not_ready":
    case "login_required":
    case "script_failed":
    case "import_failed":
    case "error":
    case "report_failed":
    case "report_timeout":
      return "danger";
    case "collecting":
    case "running":
    case "queued":
      return "warn";
    default:
      return "neutral";
  }
}

function statusText(status?: string): string {
  switch (status) {
    case "success":
      return "成功";
    case "queued":
      return "排队";
    case "collecting":
    case "running":
      return "采集中";
    case "dispatch_failed":
      return "下发失败";
    case "agent_unavailable":
      return "agent 不在线";
    case "996box_missing":
      return "缺少 996box.exe";
    case "market_cdp_disabled":
      return "CDP 开关关闭";
    case "uv_missing":
      return "缺少 uv";
    case "cdp_unavailable":
      return "CDP 不可用";
    case "page_not_ready":
      return "页面未就绪";
    case "login_required":
      return "登录失效";
    case "script_failed":
      return "脚本失败";
    case "import_failed":
      return "导入失败";
    case "report_failed":
      return "上报失败";
    case "report_timeout":
      return "上报超时";
    case "error":
      return "失败";
    default:
      return status || "未知";
  }
}

function formatTime(value?: number): string {
  if (!value) return "-";
  const ms = value < 100000000000 ? value * 1000 : value;
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(ms));
}

function formatNumber(value?: number): string {
  return new Intl.NumberFormat("zh-CN").format(value ?? 0);
}

function formatDuration(value?: number): string {
  const seconds = value ?? 0;
  if (seconds <= 0) return "-";
  if (seconds < 60) return `${seconds}秒`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds - minutes * 60;
  if (minutes < 60) return rest > 0 ? `${minutes}分${rest}秒` : `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  const minuteRest = minutes - hours * 60;
  return minuteRest > 0 ? `${hours}小时${minuteRest}分` : `${hours}小时`;
}

function formatPrice(value?: number): string {
  const n = value ?? 0;
  if (n !== 0 && Math.abs(n) < 0.01) return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 8 }).format(n);
  if (Math.abs(n) < 1) return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 6 }).format(n);
  return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2 }).format(n);
}

function formatQuantity(value?: number): string {
  const n = value ?? 0;
  return Number.isInteger(n) ? formatNumber(n) : formatPrice(n);
}

function sumStatus(rows: MarketStatusRecord[] | undefined, key: "current_count"): number {
  return (rows ?? []).reduce((sum, row) => sum + (row[key] ?? 0), 0);
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Pager<T>({ data, onPrev, onNext }: { data: PageResponse<T>; onPrev: () => void; onNext: () => void }) {
  const cursor = data.cursor ?? 0;
  const limit = data.limit ?? 50;
  const total = data.total ?? data.items.length;
  const start = total === 0 ? 0 : cursor + 1;
  const end = Math.min(total, cursor + (data.count ?? data.items.length));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[hsl(var(--muted-foreground))]">
      <div>{start}-{end} / {total} · 每页 {limit}</div>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" disabled={cursor <= 0} onClick={onPrev}>上一页</Button>
        <Button type="button" size="sm" variant="outline" disabled={!data.next_cursor} onClick={onNext}>下一页</Button>
      </div>
    </div>
  );
}

function MetricSelector({ value, onChange }: { value: HistoryMetric; onChange: (value: HistoryMetric) => void }) {
  return (
    <div className="inline-flex rounded border border-[hsl(var(--border))] p-0.5">
      {HISTORY_METRICS.map((item) => (
        <button
          key={item.value}
          type="button"
          className={`h-6 px-2 text-[11px] ${value === item.value ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function PriceSparkline({ items, metric }: { items: MarketItemHistoryRecord[]; metric: HistoryMetric }) {
  const points = items.map((item) => item[metric] ?? 0);
  if (points.length === 0) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const polyline = points
    .map((price, index) => {
      const x = points.length === 1 ? 150 : (index / (points.length - 1)) * 300;
      const y = 80 - ((price - min) / span) * 70;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <div className="mt-3">
      <svg viewBox="0 0 300 90" role="img" aria-label="单价走势" className="h-24 w-full">
        <line x1="0" y1="80" x2="300" y2="80" stroke="hsl(var(--border))" />
        <polyline fill="none" stroke="hsl(var(--primary))" strokeWidth="2" points={polyline} />
      </svg>
      <div className="flex justify-between text-xs text-[hsl(var(--muted-foreground))]">
        <span>最低 {formatMetricValue(metric, min)}</span>
        <span>最高 {formatMetricValue(metric, max)}</span>
      </div>
    </div>
  );
}

function formatMetricValue(metric: HistoryMetric, value: number): string {
  return metric === "latest_listing_count" ? formatNumber(value) : formatPrice(value);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block text-[hsl(var(--muted-foreground))]">{label}</span>
      {children}
    </label>
  );
}

function SelectField({ label, value, options, disabled, onChange }: { label: string; value: string; options: Array<[string, string]>; disabled?: boolean; onChange: (value: string) => void }) {
  return (
    <Field label={label}>
      <select className="h-9 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, labelText]) => <option key={v || "none"} value={v}>{labelText}</option>)}
      </select>
    </Field>
  );
}

function MarketComboBox({
  value,
  displayValue,
  placeholder,
  options,
  loading,
  disabled,
  onSearchChange,
  onChange,
}: {
  value: string;
  displayValue?: string;
  placeholder?: string;
  options: Array<{ value: string; label: string; meta?: string }>;
  loading?: boolean;
  disabled?: boolean;
  onSearchChange?: (value: string) => void;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const selectedLabel = displayValue || labelForValue(value, options);
  const inputText = open ? draft : selectedLabel || value;
  const trimmed = draft.trim();
  const hasExact = options.some((item) => item.value === trimmed);
  return (
    <div className="relative">
      <Input
        value={inputText}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => {
          const nextDraft = selectedLabel || value;
          setDraft(nextDraft);
          onSearchChange?.(nextDraft);
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(event) => {
          const nextDraft = event.target.value;
          setDraft(nextDraft);
          onSearchChange?.(nextDraft);
          onChange(nextDraft);
          setOpen(true);
        }}
      />
      {open && !disabled ? (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1 shadow-lg">
          {loading ? <div className="px-2 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">搜索中...</div> : null}
          {trimmed && !hasExact ? (
            <button
              type="button"
              className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-[hsl(var(--muted))]"
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(trimmed);
                setDraft(trimmed);
                onSearchChange?.(trimmed);
                setOpen(false);
              }}
            >
              <div className="font-mono">{trimmed}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">直接使用输入值</div>
            </button>
          ) : null}
          {options.map((item) => (
            <button
              type="button"
              key={item.value}
              className={cn(
                "block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-[hsl(var(--muted))]",
                item.value === trimmed && "bg-[hsl(var(--muted))]",
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(item.value);
                setDraft(item.label);
                onSearchChange?.(item.value);
                setOpen(false);
              }}
            >
              <div className="font-medium">{item.label}</div>
              <div className="font-mono text-[11px] text-[hsl(var(--muted-foreground))]">{item.meta || item.value}</div>
            </button>
          ))}
          {!loading && options.length === 0 && !trimmed ? (
            <div className="px-2 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">暂无候选</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function serverComboOption(server: GameServerOption): { value: string; label: string; meta?: string; aliases?: string[] } {
  const label = marketServerLabel(server.display_name || server.server_name, server.server_id);
  const matchedLabel = marketServerLabel(server.matched_server_name, server.matched_server_id);
  const isMemberMatch = Boolean(
    server.matched_server_id &&
      server.matched_server_id !== server.server_id &&
      matchedLabel,
  );
  const memberText = server.member_count && server.member_count > 1 ? `合区 ${server.member_count} 个区服` : "";
  return {
    value: server.server_id,
    label,
    aliases: [
      server.effective_main_server_id || "",
      server.matched_server_id || "",
      matchedLabel,
      server.matched_alias || "",
    ].filter(Boolean),
    meta: [
      server.server_id,
      memberText,
      isMemberMatch ? `匹配 ${matchedLabel}` : "",
      server.alias || "",
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

function labelForValue(value: string | undefined, options: Array<{ value: string; label: string }>): string {
  if (!value) return "";
  return options.find((item) => item.value === value)?.label || "";
}

function labelForServerValue(value: string | undefined, options: Array<{ value: string; label: string; aliases?: string[] }>): string {
  if (!value) return "";
  return options.find((item) => item.value === value || item.aliases?.includes(value))?.label || "";
}

function labelServerFromStatus(serverID: string | undefined, rows: MarketStatusRecord[] | undefined): string {
  if (!serverID) return "";
  const row = (rows ?? []).find(
    (item) => item.canonical_server_id === serverID || item.server_id === serverID,
  );
  if (!row) return "";
  if (row.canonical_server_id === serverID) {
    return marketServerLabel(row.canonical_server_name || row.server_name, "");
  }
  return marketServerLabel(row.server_name || row.canonical_server_name, "");
}

function errorText(err: unknown): string {
  return err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
}
